// Section 24.6: content hooks return Deltas and phases apply them here. One place so every
// phase applies a delta the same way.
import { clamp } from '../../core/math';
import { invalidateMemo } from '../../core/memo';
import { BUILDING_MEMO, peopleSorted } from '../../core/queries';
import type { Delta, PersonField, PersonId, PhaseContext } from '../../core/types';
import { damageBuilding } from './capacity';
import { killPerson } from './death';
import { remember } from './memory';
import { addStock } from './stocks';
import { makeTools, wearTools } from './tools';
// Sections 14 and 19 (stream E2): the gate and event effects.
import { names } from '../../content/names';
import { generateArrivalGroup } from '../../core/generate';
import { mintId } from '../../core/ids';
import { hasBuilding } from '../../core/queries';
import type { ArrivalGroup, Person } from '../../core/types';
import { num } from './value';

function adjust(ctx: PhaseContext, value: number, amount: number): number {
  return clamp(value + amount, 0, ctx.cfg.people.statMax);
}

function adjustField(
  ctx: PhaseContext,
  personId: string,
  field: PersonField,
  amount: number,
): void {
  const p = ctx.draft.people[personId as keyof typeof ctx.draft.people];
  if (p !== undefined) p[field] = adjust(ctx, p[field], amount);
}

// Section 10 tool wear and making live with the rest of the tool rules (stream M3a).
export { wearTools } from './tools';

export function applyDelta(ctx: PhaseContext, delta: Delta): void {
  const d = ctx.draft;
  switch (delta.kind) {
    case 'stock':
      addStock(ctx, delta.resource, delta.amount, delta.flow);
      return;
    case 'seed':
      d.seedReserved = Math.max(0, d.seedReserved + delta.amount);
      return;
    case 'plant':
      d.plantedPlots[delta.personId] = (d.plantedPlots[delta.personId] ?? 0) + delta.plots;
      d.fieldsPlanted += delta.plots;
      return;
    case 'service': {
      const services = ctx.report.current.services;
      services[delta.service] = (services[delta.service] ?? 0) + delta.amount;
      return;
    }
    case 'toolWear':
      wearTools(ctx, delta.amount);
      return;
    case 'person':
      adjustField(ctx, delta.personId, delta.field, delta.amount);
      return;
    case 'everyone':
      for (const p of peopleSorted(d)) p[delta.field] = adjust(ctx, p[delta.field], delta.amount);
      return;
    case 'injure': {
      const p = d.people[delta.personId];
      if (p === undefined) return;
      p.status.injured = {
        seasonsRemaining: delta.seasons ?? ctx.registry.illnesses.injury.untreatedSeasons,
        treated: false,
        sinceTurn: ctx.season.turn,
      };
      ctx.report.add({
        kind: 'injury',
        personId: p.id,
        jobId: p.job === 'rest' ? null : p.job,
        source: delta.source,
      });
      applyDeltas(ctx, ctx.registry.illnesses.injury.onCatch(ctx.live, p));
      return;
    }
    case 'sicken': {
      const p = d.people[delta.personId];
      if (p === undefined || p.status.sick !== null) return;
      const def = ctx.registry.illnesses[delta.illnessId];
      p.status.sick = {
        illnessId: delta.illnessId,
        seasonsRemaining: delta.seasons ?? def.untreatedSeasons,
        seasonsSick: 0,
        treated: false,
        sinceTurn: ctx.season.turn,
      };
      ctx.report.add({ kind: 'illness', personId: p.id, illnessId: delta.illnessId });
      applyDeltas(ctx, def.onCatch(ctx.live, p));
      return;
    }
    case 'outbreak': {
      // Section 19: a fixed number of event-stream draws, whatever the population (stream M3b).
      const pool = peopleSorted(d)
        .filter((p) => p.status.sick === null)
        .map((p) => p.id);
      const n = ctx.eventRng.int(delta.min, delta.max);
      const chosen: PersonId[] = [];
      for (let i = 0; i < n; i++) {
        const r = ctx.eventRng.float();
        if (pool.length === 0) continue;
        const [id] = pool.splice(Math.min(pool.length - 1, Math.floor(r * pool.length)), 1);
        if (id !== undefined) chosen.push(id);
      }
      for (const id of chosen.sort()) {
        applyDelta(ctx, {
          kind: 'sicken',
          personId: id,
          illnessId: delta.illnessId,
          source: delta.source,
        });
      }
      return;
    }
    case 'quarantine':
      d.quarantine.active = delta.active;
      d.quarantine.untilTurn = delta.active ? delta.untilTurn : null;
      return;
    case 'kill':
      killPerson(ctx, delta.personId, delta.cause);
      return;
    case 'building': {
      const b = d.buildings[delta.buildingId];
      if (b === undefined) return;
      b.condition = clamp(b.condition + delta.condition, 0, 100);
      invalidateMemo(d, BUILDING_MEMO);
      if (delta.condition < 0) {
        ctx.report.add({
          kind: 'building-damaged',
          buildingId: b.id,
          amount: -delta.condition,
          source: delta.source,
        });
        applyDeltas(ctx, ctx.registry.buildings[b.type].onDamaged(ctx.live, b, -delta.condition));
      }
      return;
    }
    case 'damageBuilding':
      applyDeltas(ctx, damageBuilding(ctx, delta));
      return;
    case 'makeTools':
      makeTools(ctx, delta.amount);
      return;
    case 'raidLoss': {
      // Section 13 safety need (stream M4a): only the last 4 seasons of raid losses matter.
      const keepAfter = ctx.season.turn - ctx.cfg.threats.safetyLookbackSeasons;
      d.safety.raidLosses = [
        ...d.safety.raidLosses.filter((r) => r.turn > keepAfter),
        { turn: ctx.season.turn, fraction: clamp(delta.fraction, 0, 1) },
      ];
      return;
    }
    case 'effect':
      d.activeEffects.push(delta.effect);
      return;
    // ---- Sections 14 and 19 (stream E2).
    case 'arrivals': {
      const group = generateArrivalGroup(ctx.cfg, d, names, delta.spec, ctx.season.turn);
      d.arrivalGroups[group.id] = group;
      return;
    }
    case 'gate':
      decideGroup(ctx, delta);
      return;
    case 'closeGates':
      d.counters.gatesClosedUntilTurn = Math.max(
        d.counters.gatesClosedUntilTurn ?? delta.untilTurn,
        delta.untilTurn,
      );
      return;
    case 'improvement':
      if (d.improvements.some((i) => i.id === delta.improvementId)) return;
      d.improvements.push({ id: delta.improvementId, turn: ctx.season.turn });
      ctx.report.add({ kind: 'improvement', improvementId: delta.improvementId });
      return;
    case 'planDeparture': {
      const p = d.people[delta.personId];
      if (p === undefined) return;
      // Exile (Section 13) overrides a planned emigration; otherwise the first plan stands.
      if (p.plannedDeparture !== null && delta.reason !== 'exile') return;
      p.plannedDeparture = { reason: delta.reason, atTurn: delta.atTurn };
      ctx.report.add({
        kind: 'departure-planned',
        personId: p.id,
        householdId: p.householdId,
        reason: delta.reason,
        atTurn: delta.atTurn,
      });
      return;
    }
    case 'job': {
      const p = d.people[delta.personId];
      if (p === undefined || p.job === delta.jobId) return;
      if (p.job !== 'rest')
        ctx.report.add({ kind: 'job-vacant', jobId: p.job, personId: p.id, reason: 'aged' });
      p.job = delta.jobId;
      p.streaks.inJob = 0;
      return;
    }
    case 'memory': {
      const p = d.people[delta.personId];
      if (p !== undefined) remember(ctx, p, delta.entry);
      return;
    }
    case 'prompt':
      d.openPrompts.push(delta.prompt);
      return;
    case 'report':
      ctx.report.add(delta.entry);
      return;
  }
}

export function applyDeltas(ctx: PhaseContext, deltas: readonly Delta[]): void {
  for (const delta of deltas) applyDelta(ctx, delta);
}

// ---- Section 14: the gate (stream E2). Admitted people join as households (a family arrives as
// one household) and sleep rough until the next season's housing pass assigns beds (decide phase,
// ADR-0010 item 21): admitting eight into two spare beds is a shelter crisis by design.

type GateDelta = Extract<Delta, { kind: 'gate' }>;

function admit(ctx: PhaseContext, group: ArrivalGroup, ids: PersonId[], guest: boolean): void {
  if (ids.length === 0) return;
  const d = ctx.draft;
  const turn = ctx.season.turn;
  const m = ctx.cfg.migration;
  const trust = d.norms['founders-first'].established
    ? ctx.cfg.norms['founders-first'].newcomerTrust
    : m.newcomerTrust;
  const units = group.family === true ? [ids] : ids.map((id) => [id]);
  for (const unit of units) {
    const householdId = mintId(d, 'household');
    d.households[householdId] = {
      id: householdId,
      memberIds: [...unit].sort(),
      buildingId: null,
      foundedTurn: turn,
      isNewcomer: true,
    };
    for (const id of unit) {
      const candidate = group.candidates.find((c) => c.id === id);
      if (candidate === undefined) continue;
      const p = JSON.parse(JSON.stringify(candidate)) as Person;
      p.householdId = householdId;
      p.arrivedTurn = turn;
      p.origin = 'newcomer';
      p.trust = trust;
      p.job = 'rest';
      p.partnerId = p.partnerId !== null && ids.includes(p.partnerId) ? p.partnerId : null;
      p.parentIds = p.parentIds.filter((x) => ids.includes(x));
      p.childIds = p.childIds.filter((x) => ids.includes(x));
      p.status.guest = guest ? { seasonsRemaining: m.guestSeasons } : null;
      p.memory = [{ turn, kind: 'arrived' }];
      d.people[id] = p;
    }
  }
  group.admittedIds = [...new Set([...(group.admittedIds ?? []), ...ids])].sort();
  ctx.report.current.population.arrivals += ids.length;
  const counters = d.counters;
  counters.lastArrivalTurn = turn;
  const window = m.manyAdmittedWindowSeasons;
  counters.admissions = [
    ...counters.admissions.filter((a) => a.turn > turn - window && a.turn !== turn),
    {
      turn,
      count: ids.length + (counters.admissions.find((a) => a.turn === turn)?.count ?? 0),
    },
  ];
  // Section 14: many admitted in a short window marks a newcomers faction for 8 seasons.
  const recent = counters.admissions.reduce((n, a) => n + a.count, 0);
  const before = Object.keys(d.people).length - recent;
  if (recent >= m.manyAdmittedShare * Math.max(1, before)) {
    const until = turn + m.newcomerMarkSeasons - 1;
    const mark = d.factionMarks.find((f) => f.untilTurn >= turn);
    if (mark !== undefined) mark.untilTurn = Math.max(mark.untilTurn, until);
    else d.factionMarks.push({ id: 'newcomers', sinceTurn: turn, untilTurn: until });
  }
  // Section 14: a sick arrival admitted without a clinic seeds contagion 1.5x for 2 seasons.
  const sick = ids.some((id) => (d.people[id]?.status.sick ?? null) !== null);
  if (sick && !hasBuilding(d, 'clinic')) {
    const until = turn + m.sickArrivalSeasons - 1;
    d.quarantine.contagionBoostUntilTurn = Math.max(
      d.quarantine.contagionBoostUntilTurn ?? until,
      until,
    );
  }
}

/**
 * Section 14: turning people away costs Devout people trust -2 and happiness -3; turning away a
 * starving family with children costs everyone happiness -2 (the Ledger mark is derived from the
 * `arrival` entry by the Ledger: reason famine and a candidate under 15).
 */
function turnAway(ctx: PhaseContext, group: ArrivalGroup, rejected: Person[]): void {
  if (rejected.length === 0) return;
  const m = ctx.cfg.migration;
  const max = ctx.cfg.people.statMax;
  let devout = 0;
  let total = 0;
  for (const p of peopleSorted(ctx.draft)) {
    if (!p.traits.includes('devout') || rejected.some((r) => r.id === p.id)) continue;
    const change = num(ctx.live, { kind: 'trustChange', personId: p.id, cause: 'turned-away' });
    p.trust = clamp(p.trust + change, 0, max);
    p.happiness = clamp(p.happiness + m.turnAwayDevoutHappiness, 0, max);
    devout++;
    total += change;
  }
  if (devout > 0)
    ctx.report.add({ kind: 'trust', cause: 'turned-away', delta: total / devout, people: devout });
  const starving =
    group.reason === 'famine' && rejected.some((p) => p.ageSeasons < ctx.cfg.people.adultAtSeasons);
  if (starving) {
    for (const p of peopleSorted(ctx.draft)) {
      if (rejected.some((r) => r.id === p.id)) continue;
      p.happiness = clamp(p.happiness + m.turnAwayStarvingHappiness, 0, max);
    }
  }
}

function decideGroup(ctx: PhaseContext, delta: GateDelta): void {
  const d = ctx.draft;
  const group = d.arrivalGroups[delta.groupId];
  if (group === undefined) return;
  const turn = ctx.season.turn;
  const candidateIds = group.candidates.map((c) => c.id);
  const followUp = group.status === 'guests' && group.settled !== true;
  const room = Math.max(0, ctx.cfg.migration.populationCap - Object.keys(d.people).length);
  let chosen: PersonId[];
  switch (delta.decision) {
    case 'admit-all':
    case 'guests':
      chosen = candidateIds;
      break;
    case 'admit-some':
      chosen = candidateIds.filter((id) => delta.personIds.includes(id));
      break;
    case 'turn-away':
      chosen = [];
      break;
  }
  let rejected: Person[];
  if (followUp) {
    // Section 14 guests "then decide": those not kept leave at once (the migration phase).
    const present = (group.admittedIds ?? []).filter((id) => d.people[id] !== undefined);
    rejected = [];
    for (const id of present) {
      const p = d.people[id];
      if (p === undefined || chosen.includes(id)) continue;
      rejected.push(p);
      applyDelta(ctx, { kind: 'planDeparture', personId: id, reason: 'turned-away', atTurn: turn });
    }
    group.admittedIds = present.filter((id) => chosen.includes(id));
    group.settled = true;
  } else if (delta.vanish === true) {
    // Let in, but gone before they joined (Section 19 stranger): nobody was turned away.
    rejected = [];
    group.settled = true;
  } else {
    // Arrivals stop at the population cap (Section 26): the rest are turned away.
    const admitted = chosen.slice(0, room);
    admit(ctx, group, admitted, delta.decision === 'guests');
    rejected = group.candidates.filter((c) => !admitted.includes(c.id));
    if (delta.decision !== 'guests') group.settled = true;
  }
  turnAway(ctx, group, rejected);
  group.status = delta.decision === 'guests' ? 'guests' : delta.decision;
  group.decidedTurn = turn;
  group.promptId = null;
  ctx.report.add({
    kind: 'arrival',
    groupId: group.id,
    reason: group.reason,
    personIds: candidateIds,
    decision: delta.decision,
  });
}
